import * as Sentry from '~/services/sentry';

import { capExtras } from './sanitize';
import type { Domain, ReportErrorOptions, ReportOptions } from './types';

/**
 * Domain and reason pairs already reported, for `once`.
 *
 * Per page session, which is the right granularity: a condition that cannot
 * change without a reload — an invalid device id, a discarded address — says
 * nothing new the second time, and repeating it inflates the event count well
 * past the number of users actually affected.
 */
const reportedOnce = new Set<string>();

/**
 * Severity an event leaves with, and the pool it is billed to.
 *
 * `info` is not a lesser warning: it is the level for things that went right
 * and still need counting. Successful orders used to be invisible here, so the
 * only way to ask "are people still paying" was to notice that nothing had
 * broken — which is indistinguishable from nobody trying.
 */
type ReportLevel = 'info' | 'warning' | 'error';

/**
 * A hard ceiling on what one page session can send.
 *
 * `once` and explicit dedupe are per-reporter decisions, and they only protect
 * against the floods someone thought of. This is the one that does not depend
 * on anyone being careful: a render loop, a poll that starts failing, or a
 * reporter added next year in a `forEach` all stop here. Sentry answers 429
 * once a project outruns its quota, and a 429 costs every report after it —
 * including the ones that mattered — so refusing to send is strictly better
 * than being refused.
 *
 * The per-fingerprint cap is keyed on the fingerprint rather than the reason
 * so that ten different endpoints failing stay ten reports, while the same
 * endpoint failing ten times does not.
 */
const MAX_EVENTS_PER_FINGERPRINT = 5;

/**
 * Separate pools per level, so noise cannot starve signal.
 *
 * On one shared pool a dozen endpoints failing on a bad connection — each
 * warning, each with its own fingerprint, so the per-fingerprint cap does not
 * touch them — would spend the whole session budget before the user reached
 * the payment that actually broke, and that error would then be dropped by a
 * mechanism whose entire purpose is to keep errors arriving.
 */
const MAX_EVENTS_PER_SESSION: Record<ReportLevel, number> = {
  /**
   * Deliberately the smallest pool. An `info` report is a milestone rather than
   * a problem, and a milestone that fires more than a handful of times in one
   * page session is a loop, not a funnel. Ten leaves room for a customer who
   * genuinely retries several times — the case this level exists to count —
   * while keeping the level that carries no urgency from ever crowding out the
   * two that do.
   */
  info: 10,
  warning: 40,
  error: 20,
};

const budgetNoticeSent: Record<ReportLevel, boolean> = {
  info: false,
  warning: false,
  error: false,
};
const sentPerLevel: Record<ReportLevel, number> = {
  info: 0,
  warning: 0,
  error: 0,
};
const sentPerFingerprint = new Map<string, number>();

/**
 * Spends one unit of the session's reporting budget, or refuses.
 *
 * Exhausting the session budget reports once that it happened, because an
 * issue that simply stops has no way to say whether the problem stopped or the
 * reporting did.
 */
const withinBudget = (level: ReportLevel, fingerprint: string[]): boolean => {
  if (sentPerLevel[level] >= MAX_EVENTS_PER_SESSION[level]) {
    if (!budgetNoticeSent[level]) {
      budgetNoticeSent[level] = true;
      Sentry.withScope((scope) => {
        scope.setLevel('warning');
        scope.setTag('domain', 'telemetry');
        scope.setTag('reason', 'session_budget_exhausted');
        scope.setFingerprint(['telemetry', 'session_budget_exhausted']);
        scope.setExtras({
          level,
          limit: MAX_EVENTS_PER_SESSION[level],
          lastFingerprint: fingerprint.join(':'),
          topFingerprints: [...sentPerFingerprint.entries()]
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([key, count]) => `${key} x${count}`),
        });
        Sentry.captureMessage(
          `[telemetry] Session ${level} budget exhausted`,
          'warning',
        );
      });
    }
    return false;
  }

  const key = `${level}:${fingerprint.join(':')}`;
  const spent = sentPerFingerprint.get(key) ?? 0;
  if (spent >= MAX_EVENTS_PER_FINGERPRINT) {
    return false;
  }

  sentPerFingerprint.set(key, spent + 1);
  sentPerLevel[level] += 1;
  return true;
};

/**
 * Applies the shared shape of every event: level, taxonomy tags, fingerprint,
 * bounded extras, and then exactly one capture.
 *
 * All of it happens inside a single `withScope` because the facade replays a
 * deferred scope and its report as one ordered unit; reporting outside the
 * callback would strip the scope, and nesting a second `withScope` inside it
 * records a separate entry rather than inheriting.
 */
const emit = (
  domain: Domain,
  level: ReportLevel,
  options: ReportOptions,
  capture: () => void,
): void => {
  // The counters below are module state, and on the server a module lives for
  // the whole Node process rather than one visit — an SSR prefetch would spend
  // a "session" budget that never resets and grow the fingerprint map forever.
  // The facade drops server calls anyway, so leave before touching anything.
  if (typeof window === 'undefined') {
    return;
  }

  const fingerprint = [domain, options.reason, ...(options.fingerprint ?? [])];
  // Keyed on the fingerprint, not on domain and reason alone: a call site that
  // passes both `once` and a fingerprint means "once per distinct kind", and
  // on the shorter key the first captcha error code seen would have silenced
  // every other one for the rest of the session.
  const key = fingerprint.join(':');
  if (options.once && reportedOnce.has(key)) {
    return;
  }
  if (!withinBudget(level, fingerprint)) {
    return;
  }

  // Marked spent only once it is actually going out. Marking before the budget
  // check would let a report refused by the budget suppress the one real
  // delivery `once` exists to guarantee.
  if (options.once) {
    reportedOnce.add(key);
  }

  Sentry.withScope((scope) => {
    scope.setLevel(level);
    scope.setTag('domain', domain);
    scope.setTag('reason', options.reason);
    // Explicit, so the same failure files as one issue however it was reached.
    // Left to the SDK it groups by stack, which is why a single AxiosError
    // currently occupies four issues that no search can add together.
    scope.setFingerprint(fingerprint);
    if (options.extras) {
      scope.setExtras(capExtras(options.extras));
    }
    capture();
  });
};

/** Reports a thrown value. The exception itself becomes the issue title. */
export const reportError = (
  domain: Domain,
  error: unknown,
  options: ReportErrorOptions,
): void =>
  emit(domain, options.level ?? 'error', options, () =>
    Sentry.captureException(error),
  );

/**
 * Reports a failure that produced no exception — a missing payment intent, a
 * verification that ran out of retries.
 *
 * `message` is the title, so anything interpolated into it has to already be
 * part of the fingerprint — an id or a count that is not is what turned one
 * condition into hundreds of issues before this module existed. A constant is
 * the safe default; `[api] 500 POST /orders/:id` is the exception that earns
 * it, because status, method and masked path are exactly what it groups on.
 * Everything else belongs in `extras`.
 */
export const reportWarning = (
  domain: Domain,
  message: string,
  options: ReportOptions,
): void =>
  emit(domain, 'warning', options, () =>
    Sentry.captureMessage(`[${domain}] ${message}`, 'warning'),
  );

/**
 * Records a milestone that went right and still has to be countable.
 *
 * Everything else in this module reports trouble, which leaves the healthy path
 * measurable only by the absence of failures — and "no errors" reads exactly
 * the same as "no customers". This is the level for the handful of moments
 * worth a number rather than a breadcrumb: an order created, and what it was
 * paid with.
 *
 * Use it sparingly and put the dimension you want to split by in the
 * fingerprint, not the message. Sentry files each distinct fingerprint as its
 * own issue, and on this self-hosted instance `search_events` is unavailable,
 * so an issue's event count is the only aggregate available — a fingerprint of
 * `[checkout, order_created, applePay]` is therefore the closest thing to a
 * "orders paid by Apple Pay" metric this project can have.
 *
 * Same constraint on `message` as {@link reportWarning}: it becomes the title,
 * so nothing may vary in it that the fingerprint does not already split on.
 */
export const reportInfo = (
  domain: Domain,
  message: string,
  options: ReportOptions,
): void =>
  emit(domain, 'info', options, () =>
    Sentry.captureMessage(`[${domain}] ${message}`, 'info'),
  );

/**
 * Reports a failure serious enough to be an error but that threw nothing — a
 * payment that ran out of verification retries, a missing payment intent.
 *
 * Same constraint on `message` as `reportWarning`: it is the title, so nothing
 * may vary in it that the fingerprint does not already split on.
 */
export const reportFailure = (
  domain: Domain,
  message: string,
  options: ReportOptions,
): void =>
  emit(domain, 'error', options, () =>
    Sentry.captureMessage(`[${domain}] ${message}`, 'error'),
  );

/**
 * Records a step through a flow. Never an event.
 *
 * Breadcrumbs cost nothing until something fails, and then the whole sequence
 * arrives inside the issue that matters. Reported as events instead, the
 * checkout and payment traces were 86% of this project's ingest and the reason
 * the quota was being spent on successful orders.
 */
export const trace = (
  domain: Domain,
  message: string,
  data?: Record<string, unknown>,
  /**
   * Severity of the step, not of the report — a breadcrumb is never an event.
   * A failure that stays a breadcrumb still deserves to be marked as one, so
   * the trail reads as a trail rather than a flat list of notes.
   */
  level: 'debug' | 'info' | 'warning' | 'error' = 'info',
): void =>
  Sentry.addBreadcrumb({
    category: domain,
    message,
    level,
    data: data ? capExtras(data) : undefined,
  });
